import React, { useState } from 'react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Quiz = ({ onComplete }) => {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [showEmailCapture, setShowEmailCapture] = useState(true);

  const questions = [
    {
      id: 1,
      section: "Business Foundation",
      question: "How clearly defined is your business model (who you serve, what you sell, how you earn)?",
      type: "single",
      options: [
        { value: "not-defined", label: "Not defined yet", score: 1 },
        { value: "rough-idea", label: "Rough idea", score: 2 },
        { value: "somewhat-clear", label: "Somewhat clear", score: 3 },
        { value: "very-clear", label: "Very clear", score: 4 },
        { value: "fully-documented", label: "Fully documented and working", score: 5 }
      ]
    },
    {
      id: 2,
      section: "Business Foundation",
      question: "How consistently are you generating revenue each month?",
      type: "single",
      options: [
        { value: "not-generating", label: "Not generating yet", score: 1 },
        { value: "sporadic", label: "Sporadic", score: 2 },
        { value: "consistent-low", label: "Consistent but low", score: 3 },
        { value: "consistent-growing", label: "Consistent and growing", score: 4 },
        { value: "strong-predictable", label: "Strong, predictable revenue", score: 5 }
      ]
    },
    {
      id: 3,
      section: "Business Foundation",
      question: "How well do you understand your ideal customer's journey (from discovery to loyal fan)?",
      type: "single",
      options: [
        { value: "not-at-all", label: "Not at all", score: 1 },
        { value: "rough-guess", label: "Rough guess", score: 2 },
        { value: "somewhat", label: "Somewhat", score: 3 },
        { value: "very-well", label: "Very well", score: 4 },
        { value: "deeply-mapped", label: "Deeply mapped and documented", score: 5 }
      ]
    },
    {
      id: 4,
      section: "Brand & Presence",
      question: "How would you describe your brand identity and digital presence?",
      type: "single",
      options: [
        { value: "diy-none", label: "DIY or none", score: 1 },
        { value: "basic-social", label: "Basic social profiles", score: 2 },
        { value: "decent-inconsistent", label: "Decent but inconsistent", score: 3 },
        { value: "professional-polished", label: "Professional and polished", score: 4 },
        { value: "premium-consistent", label: "Premium and consistent across all channels", score: 5 }
      ]
    },
    {
      id: 5,
      section: "Brand & Presence",
      question: "How confident are you that your messaging and visuals convert your ideal audience?",
      type: "single",
      options: [
        { value: "not-confident", label: "Not confident", score: 1 },
        { value: "unsure", label: "Unsure", score: 2 },
        { value: "somewhat", label: "Somewhat", score: 3 },
        { value: "confident", label: "Confident", score: 4 },
        { value: "extremely-confident", label: "Extremely confident", score: 5 }
      ]
    },
    {
      id: 6,
      section: "Systems & Operations",
      question: "How organized and documented are your day-to-day business operations?",
      type: "single",
      options: [
        { value: "in-head", label: "Purely in my head", score: 1 },
        { value: "loose-notes", label: "Loose notes or ad hoc tools", score: 2 },
        { value: "partially-documented", label: "Partially documented", score: 3 },
        { value: "mostly-documented", label: "Mostly documented and repeatable", score: 4 },
        { value: "fully-systemized", label: "Fully systemized and delegated", score: 5 }
      ]
    },
    {
      id: 7,
      section: "Systems & Operations",
      question: "How much of your business is automated (client onboarding, payments, content scheduling, etc.)?",
      type: "single",
      options: [
        { value: "zero-manual", label: "0% — all manual", score: 1 },
        { value: "minimal", label: "Minimal", score: 2 },
        { value: "some", label: "Some", score: 3 },
        { value: "a-lot", label: "A lot", score: 4 },
        { value: "nearly-everything", label: "Nearly everything that can be automated is", score: 5 }
      ]
    },
    {
      id: 8,
      section: "Growth Readiness",
      question: "How ready are your services/offers to scale (be delivered by a team or digitally without you doing all the work)?",
      type: "single",
      options: [
        { value: "not-at-all", label: "Not at all", score: 1 },
        { value: "not-sure-how", label: "Not sure how", score: 2 },
        { value: "somewhat", label: "Somewhat", score: 3 },
        { value: "mostly-ready", label: "Mostly ready", score: 4 },
        { value: "fully-productized", label: "Fully productized and scalable", score: 5 }
      ]
    },
    {
      id: 9,
      section: "Growth Readiness",
      question: "How confident are you in your marketing/sales engine to consistently attract new customers?",
      type: "single",
      options: [
        { value: "no-engine", label: "No engine yet", score: 1 },
        { value: "inconsistent", label: "Inconsistent", score: 2 },
        { value: "somewhat-reliable", label: "Somewhat reliable", score: 3 },
        { value: "consistently-performing", label: "Consistently performing", score: 4 },
        { value: "automated-reliable", label: "Automated and reliable", score: 5 }
      ]
    },
    {
      id: 10,
      section: "Growth Readiness",
      question: "How often do you find yourself stuck \"in the weeds\" (working in the business vs. on the business)?",
      type: "single",
      options: [
        { value: "always", label: "Always", score: 1 },
        { value: "most-days", label: "Most days", score: 2 },
        { value: "sometimes", label: "Sometimes", score: 3 },
        { value: "rarely", label: "Rarely", score: 4 },
        { value: "never-ceo", label: "Never — I operate as CEO, not operator", score: 5 }
      ]
    }
  ];

  const handleEmailSubmit = (e) => {
    e.preventDefault();
    if (email && email.includes('@')) {
      setShowEmailCapture(false);
    }
  };

  const handleAnswer = (questionId, answer, score = 0) => {
    const newAnswer = {
      question_id: questionId,
      answer: answer,
      score: score
    };

    setAnswers(prev => {
      const filtered = prev.filter(a => a.question_id !== questionId);
      return [...filtered, newAnswer];
    });
  };

  const handleMultipleAnswer = (questionId, value, score, checked) => {
    setAnswers(prev => {
      let questionAnswers = prev.find(a => a.question_id === questionId);
      
      if (!questionAnswers) {
        questionAnswers = {
          question_id: questionId,
          answer: [],
          score: 0
        };
      }

      let newAnswerArray = Array.isArray(questionAnswers.answer) ? [...questionAnswers.answer] : [];
      let newScore = questionAnswers.score || 0;

      if (checked) {
        if (!newAnswerArray.includes(value)) {
          newAnswerArray.push(value);
          newScore += score;
        }
      } else {
        newAnswerArray = newAnswerArray.filter(a => a !== value);
        newScore -= score;
      }

      const filtered = prev.filter(a => a.question_id !== questionId);
      return [...filtered, {
        question_id: questionId,
        answer: newAnswerArray,
        score: Math.max(0, newScore)
      }];
    });
  };

  const submitQuiz = async () => {
    setLoading(true);
    try {
      const quizData = {
        email: email,
        answers: answers
      };

      const response = await axios.post(`${API}/quiz/submit`, quizData);
      onComplete(response.data);
    } catch (error) {
      console.error('Quiz submission failed:', error);
      alert('Failed to submit quiz. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const nextQuestion = () => {
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      submitQuiz();
    }
  };

  const prevQuestion = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };

  const currentAnswer = answers.find(a => a.question_id === questions[currentQuestion].id);
  const canProceed = currentAnswer && (
    questions[currentQuestion].type === 'multiple' ? 
    (Array.isArray(currentAnswer.answer) && currentAnswer.answer.length > 0) :
    currentAnswer.answer
  );

  if (showEmailCapture) {
    return (
      <div className="min-h-screen bg-ivory flex items-center justify-center">
        <div className="container mx-auto px-6">
          <div className="max-w-2xl mx-auto">
            <Card className="editorial-card p-12 text-center">
              <h1 className="headline-medium text-charcoal mb-6">
                Readiness Quiz
              </h1>
              <p className="body-large text-graphite mb-8">
                Discover your path to scaling with our comprehensive 10-question assessment. We'll provide personalized recommendations based on your business foundation, brand presence, systems, and growth readiness.
              </p>
              
              <form onSubmit={handleEmailSubmit} className="max-w-md mx-auto">
                <div className="mb-6">
                  <label className="label-text text-charcoal block mb-3">
                    Email Address
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full p-4 border border-bronze-light rounded-lg focus:outline-none focus:border-bronze-strong"
                    placeholder="your@email.com"
                    required
                  />
                </div>
                
                <Button type="submit" className="btn-primary w-full">
                  Start Assessment
                </Button>
              </form>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  const question = questions[currentQuestion];
  const progress = ((currentQuestion + 1) / questions.length) * 100;

  return (
    <div className="min-h-screen bg-ivory flex items-center justify-center">
      <div className="container mx-auto px-6">
        <div className="max-w-3xl mx-auto">
          {/* Progress Bar */}
          <div className="mb-8">
            <div className="progress-bar">
              <div 
                className="progress-fill" 
                style={{ width: `${progress}%` }}
              ></div>
            </div>
            <p className="label-text text-graphite mt-2 text-center">
              Question {currentQuestion + 1} of {questions.length}
            </p>
          </div>

          <Card className="editorial-card p-12">
            {/* Section Header */}
            <div className="text-center mb-8">
              <p className="label-text text-bronze mb-2">{question.section}</p>
              <h2 className="headline-small text-charcoal mb-4">
                {question.question}
              </h2>
            </div>

            <div className="space-y-4 mb-12">
              {question.options.map((option, index) => (
                <label 
                  key={index}
                  className="flex items-start space-x-4 p-4 border border-bronze-light rounded-lg hover:border-bronze-strong cursor-pointer transition-colors"
                >
                  <input
                    type={question.type === 'multiple' ? 'checkbox' : 'radio'}
                    name={`question-${question.id}`}
                    value={option.value}
                    checked={
                      question.type === 'multiple' 
                        ? Array.isArray(currentAnswer?.answer) && currentAnswer.answer.includes(option.value)
                        : currentAnswer?.answer === option.value
                    }
                    onChange={(e) => {
                      if (question.type === 'multiple') {
                        handleMultipleAnswer(question.id, option.value, option.score || 0, e.target.checked);
                      } else {
                        handleAnswer(question.id, option.value, option.score || 0);
                      }
                    }}
                    className="mt-1"
                  />
                  <span className="body-medium text-charcoal flex-1">
                    {option.label}
                  </span>
                </label>
              ))}
            </div>

            <div className="flex justify-between">
              <Button
                variant="outline"
                className="btn-secondary"
                onClick={prevQuestion}
                disabled={currentQuestion === 0}
              >
                Previous
              </Button>
              
              <Button
                className="btn-primary"
                onClick={nextQuestion}
                disabled={!canProceed || loading}
              >
                {loading ? 'Submitting...' : 
                 currentQuestion === questions.length - 1 ? 'Get Results' : 'Next'
                }
              </Button>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Quiz;